/**
 * The frame the four feature sections sit in (Warnings, Plant Doctor, Seed
 * packets, Share), after direction A "Photo-led": the real evidence (a
 * screenshot, a photo) is the biggest thing in the band, and beside it the
 * gardener's pain in a bubble, the heading and at most three short lines (the seed-packets band adds a
 * third, `third`, styled like the second).
 *
 * On a phone the words come first, centred, with the evidence under them; on
 * a wide screen the evidence leads on the left and the words sit to its
 * right, left-aligned. A `more`, when given, finishes the first line's
 * paragraph only where there is room for it (md and up), so the phone keeps
 * to two short lines.
 *
 * Sections do not draw their own bands, so moving one in the order
 * (landingSections) needs nothing else changed. A section whose evidence
 * draws nothing (a PhotoSlot without a photo, in production) is the words
 * alone, centred.
 *
 * The remodelled page (landing.V2) sets `quote`, which shows the pain as a
 * block quote (PainQuote) instead of the bubble; and its extra bands (the
 * photo scrapbook) use the same frame.
 *
 * Used by: sections/Warnings.tsx, PlantDoctorSection.tsx, SeedPackets.tsx, Share.tsx,
 * and the remodelled page's WarningsV2, PlantDoctorV2, SeedPacketsV2, Scrapbook, ShareV2.
 *
 * @module FeatureBlock
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { PainBubble } from './LandingBlock';
import { PainQuote } from './PainQuote';

/** The parts of one feature band. */
interface FeatureBlockProps {
  /** A small icon above the heading on a phone, beside it on a wide screen. */
  icon?: React.ReactNode;
  /** What the gardener says, in their words; shown in a light sage bubble (or as a block quote, with `quote`). */
  pain: React.ReactNode;
  /** Shows the pain as a block quote (green italic, a green rule) instead of the sage bubble. */
  quote?: boolean;
  /** The heading. */
  title: string;
  /** The first short line under the heading. */
  line: React.ReactNode;
  /** The second short line, quieter than the first. */
  second?: React.ReactNode;
  /** A third short line, styled like the second. */
  third?: React.ReactNode;
  /** The rest of the first line's paragraph, shown only on a wide screen where it fits. */
  more?: React.ReactNode;
  /** A button under the lines. */
  action?: React.ReactNode;
  /** The evidence: the real screenshot or photo that shows it. */
  evidence: React.ReactNode;
  /** Gives the evidence more width on a wide screen, for two pictures side by side. */
  wide?: boolean;
}

/**
 * A feature section's band.
 *
 * @param props - See FeatureBlockProps.
 */
export const FeatureBlock: React.FC<FeatureBlockProps> = ({ icon, pain, quote = false, title, line, second, third, more, action, evidence, wide = false }) => (
  <section className="px-4 py-12 md:px-8 md:py-20">
    <div className="mx-auto flex max-w-5xl flex-col items-center gap-8 md:flex-row-reverse md:justify-center md:gap-14">
      <div className="flex w-full min-w-0 max-w-md flex-col items-center gap-4 text-center md:items-start md:text-left">
        {quote ? <PainQuote>{pain}</PainQuote> : <PainBubble>{pain}</PainBubble>}
        <div className="flex flex-col items-center gap-2 md:flex-row md:items-start md:gap-3">
          {icon && <div className="shrink-0 text-primary md:mt-1" aria-hidden="true">{icon}</div>}
          <h2 className="min-w-0 font-grow text-3xl font-extrabold leading-[1.08] tracking-tight [text-wrap:balance] md:text-[2.6rem]">{title}</h2>
        </div>
        <div className="w-full space-y-1.5 break-words text-lg md:text-xl">
          <p>
            {line}
            {more && <span className="hidden md:inline"> {more}</span>}
          </p>
          {second && <p className="text-muted-foreground">{second}</p>}
          {third && <p className="text-muted-foreground">{third}</p>}
        </div>
        {action}
      </div>
      <div className={cn('w-full min-w-0 max-w-sm shrink-0 space-y-3 empty:hidden md:max-w-none', wide ? 'md:w-[30rem]' : 'md:w-[22rem]')}>{evidence}</div>
    </div>
  </section>
);
